import {FacetFilter} from '@findhotel/sapi'
import {FilterUrlParams} from 'modules/search/types'

import {FacetsAsArray} from '@vio/core/src/sapi/types'

/**
 * Checks if a specific filter is currently applied/selected by the user.
 *
 * This function determines whether a given filter should be excluded from
 * suggestions because the user has already selected it.
 *
 * ## Special Handling:
 * - **reviewScore**: Returns true if ANY reviewScore is applied (regardless of value)
 * - **price**: Returns true if ANY price range is applied (regardless of values)
 * - **Array filters**: Checks if the specific value exists in the array
 * - **Single-value filters**: Checks for exact match
 *
 * @param filterType - The type of filter (e.g., 'facilities', 'price', 'reviewScore')
 * @param filterValue - The specific value to check (e.g., '1', '100-200')
 * @param appliedFilters - Currently active filters from URL
 * @returns true if the filter is already selected, false otherwise
 */
const isSelectedFilter = (
  filterType: keyof FilterUrlParams | 'price',
  filterValue: string,
  appliedFilters: FilterUrlParams
): boolean => {
  if (!appliedFilters) return false

  // Special case: reviewScore - if any reviewScore is selected, consider it selected
  if (filterType === 'reviewScore' && appliedFilters.reviewScore) {
    return true
  }

  // Special case: price - if any price range is selected, consider it selected
  if (
    filterType === 'price' &&
    (appliedFilters.priceMin || appliedFilters.priceMax)
  ) {
    return true
  }

  const appliedFilterValue = appliedFilters[filterType]

  if (!appliedFilterValue) return false

  // Array-type filters: check if value exists in the array
  if (Array.isArray(appliedFilterValue)) {
    return appliedFilterValue.includes(filterValue)
  }

  // Single-value filters: check for exact match
  return appliedFilterValue === filterValue
}

/**
 * Maps filter types to their corresponding facet keys in the API response.
 *
 * Some filter types use different naming in the facets object:
 * - 'starRatings' → 'starRating'
 * - 'themes' → 'themeIds'
 * - 'propertyTypes' → 'propertyTypeId'
 *
 * @param facets - Facets object from search API
 * @param filterType - The filter type key from URL params
 * @returns The corresponding facet array, or undefined if not found
 */
const getFacetFilters = (
  facets: FacetsAsArray,
  filterType: keyof FilterUrlParams
) => {
  switch (filterType) {
    case 'starRatings':
      return facets?.starRating
    case 'themes':
      return facets?.themeIds
    case 'propertyTypes':
      return facets?.propertyTypeId
    default:
      return facets?.[filterType]
  }
}

/**
 * Checks if a filter is available in the search API facets.
 *
 * A filter is considered available if:
 * 1. It exists in the facets for the current search
 * 2. It has a count \> 0 (meaning it would return results if applied)
 *
 * This ensures we only suggest filters that are actually applicable
 * to the current search results.
 *
 * @param filterType - The type of filter to check
 * @param filterValue - The specific value to check
 * @param facets - Facets object from search API response
 * @returns true if the filter would return results, false otherwise
 */
const isAvailableFacetFilter = (
  filterType: keyof FilterUrlParams,
  filterValue: string,
  facets: FacetsAsArray
): boolean => {
  const facet = getFacetFilters(facets, filterType)
  if (!facet) return false
  return facet.some(item => item.id === filterValue && item.value > 0)
}

/**
 * Checks if a property brand filter is available in the property brands list.
 *
 * Property brands (e.g., Hilton, Marriott) come from a separate data source
 * rather than the standard facets API, so they need special handling.
 *
 * @param filterType - The filter type (must be 'brandIds' to return true)
 * @param filterValue - The brand ID to check
 * @param propertyBrands - List of available property brands from API
 * @returns true if the brand exists in the list, false otherwise
 */
const isAvailablePropertyBrandFilter = (
  filterType: keyof FilterUrlParams,
  filterValue: string,
  propertyBrands: FacetFilter[]
): boolean => {
  if (filterType !== 'brandIds') return false
  return propertyBrands.some(propertyBrand => propertyBrand.id === filterValue)
}

/**
 * Checks if a neighborhood filter is available in the neighborhoods list.
 *
 * Neighborhoods are location-specific and come from a separate data source
 * rather than the standard facets API, so they need special handling.
 *
 * @param filterType - The filter type (must be 'neighborhoodIds' to return true)
 * @param filterValue - The neighborhood ID to check
 * @param neighborhoods - List of available neighborhoods for current location
 * @returns true if the neighborhood exists in the list, false otherwise
 */
const isAvailableNeighborhoodFilter = (
  filterType: keyof FilterUrlParams,
  filterValue: string,
  neighborhoods: FacetFilter[]
): boolean => {
  if (filterType !== 'neighborhoodIds') return false
  return neighborhoods.some(neighborhood => neighborhood.id === filterValue)
}

/**
 * Master availability check that combines all availability checks.
 *
 * This function determines if a filter should be shown to the user by checking
 * multiple data sources. A filter is considered available if it passes ANY of
 * these checks:
 *
 * ## Always Available (no validation needed):
 * - **price**: Price ranges are always applicable
 * - **amenities**: Amenity filters are always applicable
 * - **freeCancellation**: Free cancellation is always applicable
 * - **payLater**: Pay later option is always applicable
 * - **reviewScore**: Review scores are always applicable
 *
 * ## Validated Against Data Sources:
 * - **Standard facets**: facilities, themes, propertyTypes, starRatings (must have count \> 0)
 * - **Property brands**: brandIds (must exist in propertyBrands list)
 * - **Neighborhoods**: neighborhoodIds (must exist in neighborhoods list)
 *
 * @param filterType - The type of filter to check
 * @param filterValue - The specific value to check
 * @param facets - Facets object from search API
 * @param propertyBrands - Optional list of available property brands
 * @param neighborhoods - Optional list of available neighborhoods
 * @returns true if the filter can be shown to the user, false otherwise
 */
const isAvailableFilter = (
  filterType: keyof FilterUrlParams | 'price',
  filterValue: string,
  facets: FacetsAsArray,
  propertyBrands?: FacetFilter[],
  neighborhoods?: FacetFilter[]
): boolean => {
  return (
    // Always available filters (no validation needed)
    filterType === 'price' ||
    filterType === 'amenities' ||
    filterType === 'freeCancellation' ||
    filterType === 'payLater' ||
    filterType === 'reviewScore' ||
    filterType === 'exclusive' ||
    // Validated filters
    isAvailableFacetFilter(filterType, filterValue, facets) ||
    isAvailablePropertyBrandFilter(
      filterType,
      filterValue,
      propertyBrands ?? []
    ) ||
    isAvailableNeighborhoodFilter(filterType, filterValue, neighborhoods ?? [])
  )
}

const isAvailableBookingOptions = (
  filterType: keyof FilterUrlParams,
  isCheckInAtLeastTwoDaysAway: boolean
): boolean => {
  if (filterType === 'freeCancellation' && !isCheckInAtLeastTwoDaysAway)
    return false
  return true
}

/**
 * Retrieves and filters recently used filters to show only relevant ones
 *
 * This function takes stored filter IDs (from localStorage or cookies) and returns
 * only those that should be displayed to the user. A filter is shown if it meets
 * ALL of the following criteria:
 * 1. NOT currently selected/active
 * 2. NOT in the popular filters list (to avoid duplication)
 * 3. IS available (would return results if applied)
 *
 * The availability check uses multiple sources:
 * - Standard facets from the search API
 * - Property brands list (for brandIds filters)
 * - Neighborhoods list (for neighborhoodIds filters)
 *
 * @param storedFilters - Array of filter IDs in format "filterType:filterValue"
 *                        Example: ["facilities:1", "facilities:7", "brandIds:hilton"]
 * @param appliedFilters - Currently active filters from the URL/state
 * @param facets - Facets object from the search API response
 * @param propertyBrands - Optional list of available property brands
 * @param neighborhoods - Optional list of available neighborhoods
 * @returns Array of RecentlyUsedFilter objects that should be displayed to the user
 *
 */

export interface GetAvailableRecentlyUsedFiltersProps {
  storedFilters: string[]
  appliedFilters: FilterUrlParams
  facets: FacetsAsArray
  propertyBrands?: FacetFilter[]
  neighborhoods?: FacetFilter[]
  isCheckInAtLeastTwoDaysAway: boolean
  limit?: number
}

type GetAvailableRecentlyUsedFiltersReturn = Array<{
  id: string
  type: keyof FilterUrlParams
  value: string
}>

/**
 *
 * @param filters - Array of filter strings in "filterType:filterValue" format
 * @returns Array of filter objects in the format {id: string, type: keyof FilterUrlParams, value: string}
 */
const getMappedFilters = (
  filters: string[]
): GetAvailableRecentlyUsedFiltersReturn => {
  return (
    filters?.map(item => {
      const [filterType, filterValue] = item.split(':')
      return {
        id: item,
        type: filterType as keyof FilterUrlParams,
        value: filterValue
      }
    }) ?? []
  )
}

export const getAvailableRecentlyUsedFilters = ({
  storedFilters,
  appliedFilters,
  facets,
  propertyBrands,
  neighborhoods,
  isCheckInAtLeastTwoDaysAway
}: GetAvailableRecentlyUsedFiltersProps): GetAvailableRecentlyUsedFiltersReturn => {
  const mappedFilters = getMappedFilters(storedFilters)
  return mappedFilters.filter(
    item =>
      !isSelectedFilter(item.type, item.value, appliedFilters) &&
      isAvailableBookingOptions(item.type, isCheckInAtLeastTwoDaysAway) &&
      isAvailableFilter(
        item.type,
        item.value,
        facets,
        propertyBrands,
        neighborhoods
      )
  )
}

/**
 * Removes duplicate reviewScore and price filters, keeping only the latest one.
 *
 * When users change reviewScore or price filters, we want to keep only their
 * most recent choice, not a history of all values they've tried. This function
 * ensures only one reviewScore and one price filter exists in the list.
 *
 * ## Behavior:
 * - **reviewScore**: Only the first occurrence in the array is kept (array is newest-first)
 * - **price**: Only the first occurrence in the array is kept (array is newest-first)
 * - **All other filters**: Kept as-is (all occurrences preserved)
 *
 * ## Example:
 * ```typescript
 * Input:  ['reviewScore:9', 'facilities:1', 'price:150-250', 'reviewScore:7', 'price:100-200']
 * Output: ['reviewScore:9', 'facilities:1', 'price:150-250']
 * // Only the first (most recent) reviewScore and price are kept
 * ```
 *
 * @param filters - Array of filter strings in "filterType:filterValue" format
 * @returns Cleaned array with only one reviewScore and one price filter
 */
export const getCleanedFilters = (filters: string[]): string[] => {
  const types = ['reviewScore', 'price']
  const cleanedFilters = new Set<string>()
  const hasAlreadyAdded = new Map<string, boolean>([])

  filters.forEach(filter => {
    const [filterType] = filter.split(':')

    // For reviewScore and price: only add the first occurrence
    if (types.includes(filterType)) {
      if (!hasAlreadyAdded.get(filterType)) {
        cleanedFilters.add(filter)
        hasAlreadyAdded.set(filterType, true)
      }
    } else {
      // For all other filters: add all occurrences
      cleanedFilters.add(filter)
    }
  })

  return [...cleanedFilters]
}

export const getValidMappedAllRecentlyUsedFilters = (
  filters: string[],
  neighborhoods: FacetFilter[],
  propertyBrands: FacetFilter[]
): GetAvailableRecentlyUsedFiltersReturn => {
  return getMappedFilters(filters).filter(item => {
    if (item.type === 'brandIds') {
      return isAvailablePropertyBrandFilter(
        item.type,
        item.value,
        propertyBrands ?? []
      )
    }
    if (item.type === 'neighborhoodIds') {
      return isAvailableNeighborhoodFilter(
        item.type,
        item.value,
        neighborhoods ?? []
      )
    }
    return true
  })
}
